/**
 * How long to keep re-aligning after load. Embeds (channels, webinars) render
 * their real height only once their scripts run, which can land well after
 * the browser's own jump to the hash target.
 */
export const SETTLE_WINDOW_MS = 10_000;

/** Any of these means the visitor has taken over the scroll position. */
export const USER_SCROLL_EVENTS = [
  'wheel',
  'touchstart',
  'pointerdown',
  'keydown',
] as const;

type ResizeObserverLike = {
  observe: (el: Element) => void;
  disconnect: () => void;
};

export type KeepHashTargetInViewEnv = {
  hash: string;
  document: Pick<Document, 'getElementById' | 'body'>;
  addEventListener: Window['addEventListener'];
  removeEventListener: Window['removeEventListener'];
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
  requestAnimationFrame: (fn: () => void) => number;
  cancelAnimationFrame: (id: number) => void;
  createResizeObserver: (onResize: () => void) => ResizeObserverLike;
};

const targetIdFromHash = (hash: string) => {
  try {
    return decodeURIComponent(hash.replace(/^#/, ''));
  } catch {
    return '';
  }
};

/**
 * On a page opened with a `#hash`, the browser scrolls to the target once,
 * then content above it that loads late pushes the target down the page.
 * This re-aligns the target every time the page changes size, until the
 * visitor scrolls themselves or the settle window ends.
 *
 * Returns a function that stops it early.
 */
export const keepHashTargetInView = (
  env: KeepHashTargetInViewEnv,
  settleWindowMs = SETTLE_WINDOW_MS,
): (() => void) => {
  const id = targetIdFromHash(env.hash);
  const target = id ? env.document.getElementById(id) : null;
  if (!target) return () => {};

  let frame: number | null = null;
  let timer: unknown = null;
  let stopped = false;

  const realign = () => {
    if (stopped) return;
    if (frame !== null) env.cancelAnimationFrame(frame);
    frame = env.requestAnimationFrame(() => {
      frame = null;
      // Instant, not the root's smooth scroll: a smooth scroll per resize
      // would visibly chase the target down the page.
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
  };

  const observer = env.createResizeObserver(realign);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    observer.disconnect();
    if (frame !== null) env.cancelAnimationFrame(frame);
    frame = null;
    if (timer !== null) env.clearTimeout(timer);
    timer = null;
    for (const type of USER_SCROLL_EVENTS) {
      env.removeEventListener(type, stop, { capture: true });
    }
    env.removeEventListener('hashchange', stop);
  };

  for (const type of USER_SCROLL_EVENTS) {
    env.addEventListener(type, stop, { capture: true, passive: true });
  }
  // A new hash is a new destination; the browser handles that jump itself.
  env.addEventListener('hashchange', stop);
  timer = env.setTimeout(stop, settleWindowMs);
  observer.observe(env.document.body);

  return stop;
};

export const browserEnv = (): KeepHashTargetInViewEnv => ({
  hash: window.location.hash,
  document,
  addEventListener: window.addEventListener.bind(window),
  removeEventListener: window.removeEventListener.bind(window),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (id) => window.clearTimeout(id as number),
  requestAnimationFrame: (fn) => window.requestAnimationFrame(fn),
  cancelAnimationFrame: (id) => window.cancelAnimationFrame(id),
  createResizeObserver: (onResize) => new ResizeObserver(onResize),
});
